import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { knowledgeBasesApi, kbTagsApi } from '../utils/api';
import type { KnowledgeBase, KBTagTreeNode } from '../types/knowledgeBase';
import { KBResourceType } from '../types/knowledgeBase';
import Navigation from '../components/Navigation';
import FileUploader from '../components/FileUploader';
import TagSelector from '../components/TagSelector';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function KnowledgeBaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [kb, setKb] = useState<KnowledgeBase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // URL add state
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [urlName, setUrlName] = useState('');
  const [urlPath, setUrlPath] = useState('');
  const [isAddingUrl, setIsAddingUrl] = useState(false);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Tags state
  const [availableTags, setAvailableTags] = useState<KBTagTreeNode[]>([]);

  const loadTags = async () => {
    try {
      const data = await kbTagsApi.getTree();
      setAvailableTags(data);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  const loadKB = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const data = await knowledgeBasesApi.getById(parseInt(id));
      setKb(data);
      setEditTitle(data.title);
      setEditDescription(data.description || '');
      setError('');
    } catch (err) {
      setError('Failed to load knowledge base');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadKB();
    loadTags();
  }, [id]);

  const handleAddTag = async (tagId: number) => {
    if (!id) return;
    try {
      await knowledgeBasesApi.addTag(parseInt(id), tagId);
      await loadKB();
    } catch (err) {
      console.error('Failed to add tag:', err);
      alert('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    if (!id) return;
    try {
      await knowledgeBasesApi.removeTag(parseInt(id), tagId);
      await loadKB();
    } catch (err) {
      console.error('Failed to remove tag:', err);
      alert('Failed to remove tag');
    }
  };

  const handleFileUpload = async () => {
    if (!id || selectedFiles.length === 0) return;

    try {
      setIsUploading(true);
      await knowledgeBasesApi.uploadFiles(parseInt(id), selectedFiles);
      setSelectedFiles([]);
      await loadKB();
    } catch (err) {
      console.error('Failed to upload files:', err);
      alert('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !urlName.trim() || !urlPath.trim()) return;

    try {
      setIsAddingUrl(true);
      await knowledgeBasesApi.addUrl(parseInt(id), {
        resource_name: urlName.trim(),
        resource_path: urlPath.trim(),
      });
      setUrlName('');
      setUrlPath('');
      setShowUrlForm(false);
      await loadKB();
    } catch (err) {
      console.error('Failed to add URL:', err);
      alert('Failed to add URL');
    } finally {
      setIsAddingUrl(false);
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    if (!id || !confirm('Are you sure you want to delete this resource?')) return;

    try {
      await knowledgeBasesApi.deleteResource(parseInt(id), resourceId);
      await loadKB();
    } catch (err) {
      console.error('Failed to delete resource:', err);
      alert('Failed to delete resource');
    }
  };

  const handleSaveEdit = async () => {
    if (!id || !editTitle.trim()) return;

    try {
      setIsSaving(true);
      await knowledgeBasesApi.update(parseInt(id), {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
      });
      setIsEditing(false);
      await loadKB();
    } catch (err) {
      console.error('Failed to update knowledge base:', err);
      alert('Failed to update knowledge base');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!id || !kb) return;

    try {
      if (kb.archived) {
        await knowledgeBasesApi.unarchive(parseInt(id));
      } else {
        if (!confirm('Are you sure you want to archive this knowledge base?')) return;
        await knowledgeBasesApi.archive(parseInt(id));
      }
      await loadKB();
    } catch (err) {
      console.error('Failed to archive/unarchive knowledge base:', err);
      alert('Failed to archive/unarchive knowledge base');
    }
  };

  const handleToggleDefault = async () => {
    if (!id || !kb) return;

    try {
      if (kb.is_default) {
        await knowledgeBasesApi.unsetAsDefault(parseInt(id));
      } else {
        await knowledgeBasesApi.setAsDefault(parseInt(id));
      }
      await loadKB();
    } catch (err) {
      console.error('Failed to toggle default status:', err);
      alert('Failed to update default status');
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Are you sure you want to delete this knowledge base? This action cannot be undone.')) return;

    try {
      await knowledgeBasesApi.delete(parseInt(id));
      navigate('/knowledge-bases');
    } catch (err) {
      console.error('Failed to delete knowledge base:', err);
      alert('Failed to delete knowledge base');
    }
  };

  const handleDownloadResource = async (resourceId: number, fileName: string) => {
    if (!id) return;

    try {
      const blob = await knowledgeBasesApi.downloadResource(parseInt(id), resourceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download resource:', err);
      alert('Failed to download resource');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  if (error || !kb) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Knowledge base not found'}</p>
          <Link to="/knowledge-bases" className="mt-4 inline-block text-blue-600">
            Back to Knowledge Bases
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{kb.title} - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-3xl font-bold border-b-2 border-gray-300 focus:border-black outline-none"
                />
              ) : (
                <div className="flex items-center space-x-4">
                  <h1 className="text-3xl font-bold">{kb.title}</h1>
                  {kb.archived && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                      Archived
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="px-4 py-2 btn-primary rounded-md text-sm"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 btn-secondary rounded-md text-sm"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 btn-secondary rounded-md text-sm"
                  >
                    Edit
                  </button>
                  {!kb.archived && (
                    <button
                      onClick={handleToggleDefault}
                      className={`px-4 py-2 rounded-md text-sm ${
                        kb.is_default
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'btn-secondary'
                      }`}
                    >
                      {kb.is_default ? '★ Default' : 'Set as Default'}
                    </button>
                  )}
                  <button
                    onClick={handleArchive}
                    className="px-4 py-2 btn-secondary rounded-md text-sm"
                  >
                    {kb.archived ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditing ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-md"
              placeholder="Description (optional)"
            />
          ) : (
            kb.description && (
              <p className="text-gray-600">{kb.description}</p>
            )
          )}

          {kb.source_task_id && (
            <Link
              to={`/tasks/${kb.source_task_id}`}
              className="text-sm text-blue-600 hover:underline mt-2 inline-block"
            >
              ← From Task #{kb.source_task_id}
            </Link>
          )}
        </div>

        {/* Tags Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium">Tags</h2>
            <Link
              to="/kb-tags"
              className="text-sm text-blue-600 hover:underline"
            >
              Manage tags
            </Link>
          </div>
          <TagSelector
            availableTags={availableTags}
            selectedTags={kb.tags || []}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            disabled={kb.archived}
          />
        </div>

        {/* Upload Files Section */}
        {!kb.archived && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-medium mb-4">Add Resources</h2>

            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Files
                </label>
                <FileUploader
                  onFilesChange={setSelectedFiles}
                  maxFiles={20}
                  maxSize={10 * 1024 * 1024}
                />
                {selectedFiles.length > 0 && (
                  <button
                    onClick={handleFileUpload}
                    disabled={isUploading}
                    className="mt-4 px-6 py-2 btn-primary rounded-md text-sm disabled:opacity-50"
                  >
                    {isUploading ? 'Uploading...' : 'Upload Files'}
                  </button>
                )}
              </div>

              {/* URL Add */}
              <div>
                <button
                  onClick={() => setShowUrlForm(!showUrlForm)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {showUrlForm ? '− Cancel' : '+ Add URL'}
                </button>

                {showUrlForm && (
                  <form onSubmit={handleAddUrl} className="mt-3 space-y-3">
                    <input
                      type="text"
                      value={urlName}
                      onChange={(e) => setUrlName(e.target.value)}
                      placeholder="URL title"
                      className="w-full px-4 py-2 border border-gray-200 rounded-md"
                      required
                    />
                    <input
                      type="url"
                      value={urlPath}
                      onChange={(e) => setUrlPath(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 border border-gray-200 rounded-md"
                      required
                    />
                    <button
                      type="submit"
                      disabled={isAddingUrl}
                      className="px-4 py-2 btn-primary rounded-md text-sm disabled:opacity-50"
                    >
                      {isAddingUrl ? 'Adding...' : 'Add URL'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resources List */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-medium mb-4">
            Resources ({kb.resources.length})
          </h2>

          {kb.resources.length === 0 ? (
            <p className="text-gray-500">No resources yet</p>
          ) : (
            <div className="space-y-3">
              {kb.resources.map((resource) => (
                <div
                  key={resource.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:bg-gray-50 group"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {resource.resource_type === KBResourceType.FILE ? '📄' : '🔗'}
                      </span>
                      <div className="flex-1">
                        {resource.resource_type === KBResourceType.FILE ? (
                          <div>
                            {resource.resource_name.endsWith('.tool') ? (
                              <span className="font-medium text-gray-700">
                                {resource.resource_name}
                              </span>
                            ) : (
                              <a
                                href={`/knowledge-bases/${id}/resources/${resource.id}/preview`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-blue-600 hover:underline"
                              >
                                {resource.resource_name}
                              </a>
                            )}
                            {resource.file_size && (
                              <p className="text-sm text-gray-500">
                                {formatFileSize(resource.file_size)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium">{resource.resource_name}</p>
                            <a
                              href={resource.resource_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline break-all"
                            >
                              {resource.resource_path}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {resource.resource_type === KBResourceType.FILE && !resource.resource_name.endsWith('.tool') && (
                      <button
                        onClick={() => handleDownloadResource(resource.id, resource.resource_name)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-white"
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    )}
                    {!kb.archived && (
                      <button
                        onClick={() => handleDeleteResource(resource.id)}
                        className="text-red-600 hover:text-red-800 text-sm px-3 py-1"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
